import apiClient from './apiClient';

export interface QsCertificateLine {
  id: string;
  projectId?: string;
  wbsId?: number;
  taskId?: string;
  costCodeId?: string;
  lineCode: string;
  description: string;
  status: 'pending' | 'certified' | 'rejected';
  quantity: number;
  unitOfMeasure?: string;
  certifiedValue: number;
  percentCertified?: number;
  certifiedAt?: string;
  retentionPercent: number;
  retentionAmount: number;
  netCertifiedValue: number;
  notes?: string;
  createdAt?: string;
  wbs?: {
    id: number;
    code: string;
    name: string;
  };
  task?: {
    id: string;
    code?: string;
    taskCode?: string;
    name: string;
    status: string;
    completionPercentage: number;
  };
  costCode?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface CreateQsCertificateLineRequest {
  projectId: string;
  wbsId?: number;
  taskId?: string;
  costCodeId?: string;
  lineCode: string;
  description: string;
  quantity: number;
  unitOfMeasure?: string;
  certifiedValue: number;
  retentionPercent: number;
  notes?: string;
}

export interface DrawdownPreview {
  project: {
    id: string;
    name: string;
    projectCode?: string;
  };
  drawdownReference?: string;
  groupBy: 'reference' | 'wbs';
  generatedAt: string;
  totals: {
    grossAmount: number;
    retentionAmount: number;
    netAmount: number;
    itemCount: number;
    groupCount: number;
  };
  groups: Array<{
    key: string;
    label: string;
    totals: {
      grossAmount: number;
      retentionAmount: number;
      netAmount: number;
    };
    itemCount: number;
    items: Array<{
      id: string;
      description: string;
      status: string;
      readinessChecklist?: Record<string, boolean>;
      amounts: {
        grossAmount: number;
        retentionAmount: number;
        netAmount: number;
      };
    }>;
  }>;
}

export interface ClaimableCertifiedBqLine {
  id: string;
  projectId: string;
  bqCode: string;
  description: string;
  elementGroup?: string | null;
  subElement?: string | null;
  unit?: string | null;
  currency: string;
  certifiedBqValue: number;
  supportedWorkToDateValue: number;
  previouslyCertifiedValue: number;
  previouslyCertifiedPercent: number;
  proposedPercentCertified: number;
  workThisCertificateValue: number;
  materialsOnSiteValue: number;
  approvedVariationsValue: number;
  thisCertificateValue: number;
  maximumThisCertificateValue: number;
  toCompleteValue: number;
  weightedProgressPercent: number;
  readiness: {
    status: 'ready' | 'blocked';
    blockingReason?: string | null;
    checks?: Array<Record<string, any>>;
  };
  mappedTasks: Array<{
    id: string;
    taskCode: string;
    name: string;
    lifecycleStatus: string;
    paymentStatus: string;
    completionPercentage: number;
    weightPercent: number;
  }>;
  materials?: Array<{
    id: string;
    taskId: string;
    description?: string | null;
    claimableValue: number;
    status: string;
    source?: string | null;
  }>;
  approvedVariations?: Array<{
    id: string;
    variationNumber: string;
    title: string;
    costDelta: number;
    status: string;
  }>;
  previousCertificates?: Array<{
    id: string;
    lineCode: string;
    status: string;
    certifiedValue: number;
    percentCertified: number;
    certifiedAt?: string;
    rejectionReason?: string;
  }>;
}

export interface RecommendationForPaymentPreview {
  projectId: string;
  certificateNumber: number;
  generatedAt: string;
  terms: {
    mobilizationRecoveryCertificateCount: number;
    mobilizationRecoveryPercent: number;
    ongoingRetentionPercent: number;
  };
  groups: Array<{
    key: string;
    label: string;
    certifiedWorkToDate: number;
    thisCertificateValue: number;
    materialsOnSiteValue: number;
    approvedVariationsValue: number;
    lines: ClaimableCertifiedBqLine[];
  }>;
  totals: {
    certifiedWorkToDate: number;
    materialsOnSiteValue: number;
    approvedVariationsValue: number;
    subtotalBeforeDeductions: number;
    mobilizationRecovery: number;
    previousPayments: number;
    ongoingRetention: number;
    amountDue: number;
  };
  deductions: Array<{ label: string; amount: number }>;
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: unknown): T => {
  const body = response as ApiEnvelope<T> | T;
  return body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'data')
    ? ((body as ApiEnvelope<T>).data as T)
    : (body as T);
};

class QsService {
  async getCertificateLines(projectId: string): Promise<QsCertificateLine[]> {
    const response = await apiClient.get('/qs/certificate-lines', {
      params: { projectId },
    });
    return unwrap<QsCertificateLine[]>(response) || [];
  }

  async createCertificateLine(data: CreateQsCertificateLineRequest): Promise<QsCertificateLine> {
    const response = await apiClient.post('/qs/certificate-lines', data);
    return unwrap<QsCertificateLine>(response);
  }

  async certifyCertificateLine(id: string, percentCertified?: number): Promise<QsCertificateLine> {
    const body: Record<string, number> = {};
    if (percentCertified !== undefined && percentCertified !== null) {
      body.percentCertified = percentCertified;
    }
    const response = await apiClient.post(`/qs/certificate-lines/${id}/certify`, body);
    return unwrap<QsCertificateLine>(response);
  }

  async rejectCertificateLine(id: string, reason: string): Promise<QsCertificateLine> {
    const response = await apiClient.post(`/qs/certificate-lines/${id}/reject`, { reason });
    return unwrap<QsCertificateLine>(response);
  }

  async createDrawdownItem(id: string, drawdownReference: string): Promise<void> {
    await apiClient.post(`/qs/certificate-lines/${id}/drawdown-item`, { drawdownReference });
  }

  async getDrawdownPreview(
    projectId: string,
    options: { groupBy?: 'reference' | 'wbs'; drawdownReference?: string } = {}
  ): Promise<DrawdownPreview> {
    const response = await apiClient.get(`/reports/project/${projectId}/drawdown-preview`, {
      params: options,
    });
    return unwrap<DrawdownPreview>(response);
  }

  async downloadDrawdownPacketPdf(projectId: string, drawdownReference?: string): Promise<Blob> {
    const response = await apiClient.get(`/reports/project/${projectId}/drawdown-packet.pdf`, {
      params: { drawdownReference },
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  async createDrawdownPacketDocument(projectId: string, drawdownReference?: string): Promise<any> {
    const response = await apiClient.post(`/reports/project/${projectId}/drawdown-packet/document`, {
      drawdownReference,
    });
    return unwrap<any>(response);
  }

  async updateDrawdownItemReadiness(
    id: string,
    readinessChecklist: Record<string, boolean>
  ): Promise<void> {
    await apiClient.patch(`/qs/drawdown-items/${id}/readiness`, { readinessChecklist });
  }

  async getClaimableCertifiedBqLines(projectId: string): Promise<ClaimableCertifiedBqLine[]> {
    const response = await apiClient.get('/qs/claimable-certified-bq-lines', {
      params: { projectId },
    });
    return unwrap<ClaimableCertifiedBqLine[]>(response) || [];
  }

  async getClaimableCertifiedBqLine(id: string): Promise<ClaimableCertifiedBqLine> {
    const response = await apiClient.get(`/qs/claimable-certified-bq-lines/${id}`);
    return unwrap<ClaimableCertifiedBqLine>(response);
  }

  async certifyClaimableCertifiedBqLine(
    id: string,
    data: { percentCertified?: number; certifiedValue?: number; notes?: string }
  ): Promise<QsCertificateLine> {
    const response = await apiClient.post(`/qs/claimable-certified-bq-lines/${id}/certify`, data);
    return unwrap<QsCertificateLine>(response);
  }

  async rejectClaimableCertifiedBqLine(id: string, reason: string): Promise<QsCertificateLine> {
    const response = await apiClient.post(`/qs/claimable-certified-bq-lines/${id}/reject`, { reason });
    return unwrap<QsCertificateLine>(response);
  }

  async getRecommendationForPaymentPreview(
    projectId: string,
    options: { certificateNumber?: number; previousPayments?: number } = {}
  ): Promise<RecommendationForPaymentPreview> {
    const response = await apiClient.get('/qs/recommendation-for-payment/preview', {
      params: { projectId, ...options },
    });
    return unwrap<RecommendationForPaymentPreview>(response);
  }
}

export default new QsService();
